<template>
  <div @mouseenter="mouseoverHandler" @mouseleave="mouseoutHandler">
    <!-- `attach` keeps the snackbar inside the fixed top-right `.notification-wrapper`
         that stacks them (Vuetify 3 would teleport it to the body as a bottom overlay). -->
    <v-snackbar
      v-model="visibility"
      :color="type"
      class="notification notification--auto-height"
      :timeout="timeout"
      variant="outlined"
      attach
    >
      <div class="notification__header">
        <div class="notification__status">
          <v-icon
            :color="type"
            class="mr-2"
          >
            mdi-{{ isSuccess ? 'check-circle' : 'alert-octagon-outline' }}
          </v-icon>
          {{ notification.title }}
        </div>
        <v-btn
          icon
          variant="text"
          size="small"
          @click="deleteMsg"
        >
          <v-icon :color="type">
            mdi-close
          </v-icon>
        </v-btn>
      </div>
      <div class="ml-8 mr-5">
        <span
          class="small-text notification__text"
          @click="messageClickHandler"
          v-html="notification.message"
        />
      </div>
      <div v-if="!isSuccess && notification.status !== 0" class="text-right">
        <v-btn
          variant="text"
          border
          color="red"
          size="small"
          @click="openBugRequest"
        >
          Create Bug Request
        </v-btn>
      </div>
      <div v-if="false" class="progress mt-4" :style="{'animation-duration': `${animationDuration}ms`}">
        <!-- Ask from Trey not to show this because they think that smth is loading-->
        <v-progress-linear
          :color="type"
          value="100"
        />
      </div>
    </v-snackbar>
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';
import { mapState, mapMutations } from 'vuex';

import { IAppNotification } from '@/utils/interfaces';
import { NotificationMutations } from '@/store/types/mutation-types';
import { AppNotificationTypeEnum } from '@/bundles/App/enums/appNotificationTypeEnum';
import { Nullable } from '@/utils/types';

interface IAppNotificationData {
  timeout: number;
  timeAliveStart: number;
  timeAliveEnd: Nullable<number>;
}

const timeoutInitial = 3500;

export default defineComponent({
  name: 'AppNotification',

  props: {
    notificationId: {
      type: String,
      default: '',
    },
    index: {
      type: Number,
      default: 1,
    },
    notification: {
      type: Object as PropType<IAppNotification>,
      default: () => ({
        message: '',
        status: 400,
        title: 'Error',
        url: ''
      }),
    },
  },

  data: (): IAppNotificationData => ({
    timeout: timeoutInitial,
    timeAliveStart: Date.now(),
    timeAliveEnd: null,
  }),

  computed: {
    ...mapState({
      notifications: (state: any) => state.Notifications.notifications
    }),
    isSuccess: function (): boolean {
      if (typeof this.notification.status === 'number') {
        return (this.notification.status >= 200 && this.notification.status < 300);
      }

      return false;
    },
    visibility: {
      get (): boolean {
        return !!this.notifications[this.notificationId];
      },
      set (val: boolean) {
        if (!val) {
          this.deleteMsg();
        }
      },
    },
    type (): AppNotificationTypeEnum {
      return this.isSuccess ? AppNotificationTypeEnum.success : AppNotificationTypeEnum.error;
    },
    animationDuration (): number {
      return timeoutInitial;
    }
  },

  methods: {
    ...mapMutations('Notifications', {
      deleteNotification: NotificationMutations.DELETE_NOTIFICATION
    }),
    deleteMsg () {
      this.deleteNotification(this.notificationId);
    },
    openBugRequest () {
      // @ts-ignore
      this.$eventBus.$emit('open-request-form');
      this.$nextTick(() => {
        // @ts-ignore
        this.$eventBus.$emit('open-bug-report');
        // @ts-ignore
        this.$eventBus.$emit('fill-bug-report', {
          message: this.notification.message,
          url: this.notification.url
        });
        this.deleteMsg();
      })
    },
    messageClickHandler ($event: any) {
      if (!$event?.target?.closest('a')) {
        return;
      }

      this.deleteMsg();
    },
    mouseoverHandler () {
      this.timeAliveEnd = Date.now();
    },
    mouseoutHandler () {
      if (this.timeAliveEnd === null) {
        return;
      }

      const diff = this.timeAliveEnd - this.timeAliveStart;
      const timeout = this.timeout - diff;

      this.timeout = timeout < 0 ? 1 : timeout;
      this.timeAliveStart = Date.now();
      this.timeAliveEnd = null;
    }
  },
});
</script>

<style lang="scss" scoped>
  .notification {
    // Vuetify 2's outlined sheet kept a white surface; Vuetify 3's outlined
    // variant is transparent, which read as a half-transparent toast over the page.
    :deep(.v-snackbar__wrapper) {
      background-color: rgb(var(--v-theme-surface));
    }

    .progress {
      width: 0;
      animation-name: widthTransition;
      animation-timing-function: linear;
      animation-fill-mode: forwards;
    }

    &:hover {
      .progress {
        animation-play-state: paused;
      }
    }
  }

  @keyframes widthTransition {
    from {
      width: 0%;
    }
    to {
      width: 100%;
    }
  }
</style>
